'use client';

import { Alert, Button, Group, Text, ActionIcon, Transition } from '@mantine/core';
import { IconX, IconArrowRight, IconClock } from '@tabler/icons-react';
import { useIncompleteApplication } from '@/hooks/useIncompleteApplication';
import { useState, useEffect } from 'react';

const DISMISS_KEY = 'incompleteAppBannerDismissed';
const DISMISS_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

export function IncompleteApplicationBanner() {
  const { incompleteApp, loading, isOnApplicationPage, dismissApplication, continueApplication } = useIncompleteApplication();
  const [isDismissed, setIsDismissed] = useState(false);
  const [mounted, setMounted] = useState(false);

  // Handle client-side mounting
  useEffect(() => {
    setMounted(true);
    
    // Check if banner was recently dismissed
    const dismissedData = localStorage.getItem(DISMISS_KEY);
    if (dismissedData) {
      try {
        const { timestamp, token } = JSON.parse(dismissedData);
        const now = Date.now();
        
        // If dismissed less than 24 hours ago and for the same application, keep it dismissed
        if (now - timestamp < DISMISS_DURATION && token === incompleteApp?.token) {
          setIsDismissed(true);
        } else {
          // Clear expired dismissal
          localStorage.removeItem(DISMISS_KEY);
        }
      } catch {
        // Invalid data, clear it
        localStorage.removeItem(DISMISS_KEY);
      }
    }
  }, [incompleteApp?.token]);

  const handleDismiss = () => {
    if (incompleteApp) {
      // Store dismissal with timestamp and token
      localStorage.setItem(DISMISS_KEY, JSON.stringify({
        timestamp: Date.now(),
        token: incompleteApp.token
      }));
    }
    setIsDismissed(true);
  };

  const handleContinue = () => {
    continueApplication();
  };

  const handlePermanentDismiss = () => {
    dismissApplication();
    setIsDismissed(true);
  };

  // Don't render on server or if not mounted
  if (!mounted || loading || !incompleteApp || isOnApplicationPage || isDismissed) {
    return null;
  }

  return (
    <Transition mounted={true} transition="slide-down" duration={300}>
      {(styles) => (
        <Alert
          style={styles}
          variant="light"
          color="blue"
          icon={<IconClock size={18} />}
          styles={{
            root: {
              borderRadius: 0,
              borderTop: 'none',
              borderLeft: 'none',
              borderRight: 'none',
              borderBottom: '1px solid var(--mantine-color-blue-3)',
              backgroundColor: 'var(--mantine-color-blue-0)',
            },
            wrapper: {
              alignItems: 'center',
            },
            body: {
              flex: 1,
            },
          }}
        >
          <Group justify="space-between" align="center" wrap="nowrap">
            <Group gap="sm" align="center">
              <div>
                <Text size="sm" fw={500}>
                  You have an incomplete mentorship application
                </Text>
                <Text size="xs" c="dimmed">
                  Continue your mentorship application to connect with your chosen mentor
                </Text>
              </div>
            </Group>

            <Group gap="xs" align="center">
              <Button
                size="xs"
                variant="light"
                rightSection={<IconArrowRight size={14} />}
                onClick={handleContinue}
              >
                Continue
              </Button>
              
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                onClick={handleDismiss}
                title="Dismiss for 24 hours"
              >
                <IconX size={14} />
              </ActionIcon>
            </Group>
          </Group>
        </Alert>
      )}
    </Transition>
  );
}
